import {
  Button,
  ButtonProps,
  buttonBaseClasses,
  IconButton,
  IconButtonProps,
  styled,
  Theme,
} from '@chronosphereio/chrono-ui';
import { forwardRef } from 'react';

export interface CombinedControlsIconButtonProps extends Omit<IconButtonProps, 'variant' | 'size'> {}

/**
 * Internal-only wrapper for icon buttons that should be used in concert with `CombinedControls` for
 * a consistent look and feel.
 */
export const CombinedControlsIconButton = forwardRef<HTMLButtonElement, CombinedControlsIconButtonProps>(
  function CombinedControlsIconButton(props, ref) {
    return <CombinedControlsIconButtonWrapper {...props} ref={ref} variant="secondary" size="medium" />;
  }
);

export interface CombinedControlsButtonProps extends Omit<ButtonProps, 'variant' | 'size'> {}

/**
 * Internal-only wrapper for buttons that should be used in concert with `CombinedControls` for
 * a consistent look and feel.
 */
export const CombinedControlsButton = forwardRef<HTMLButtonElement, CombinedControlsButtonProps>(
  function CombinedControlsIconButton(props, ref) {
    return <CombinedControlsButtonWrapper {...props} ref={ref} variant="secondary" size="small" />;
  }
);

const getCommonCombinedButtonStyles = (theme: Theme) => {
  const inputPalette = theme.palette.input;
  const useInputColors = theme.featureFlags.panwRebrandStubYear;

  return {
    background: useInputColors ? inputPalette.backgroundColor : theme.palette.background.container,

    ...(useInputColors && {
      borderColor: 'transparent',
      marginRight: '1px',
      '&:hover': {
        background: inputPalette.hoverBackgroundColor,
        borderColor: 'transparent',
      },
      '&:disabled': {
        background: inputPalette.disabledBackgroundColor,
        borderColor: 'transparent',
      },
    }),

    [`&.${buttonBaseClasses.focusVisible}`]: {
      // [legacy] Adds a border highlight (similar to what the select does) for consistency.
      borderColor: useInputColors ? 'transparent' : theme.palette.primary.main,
      // [legacy] Simulates an increase of border size to 2px on focus without needing to
      // fight with the height/width of the button changing when going from 1px to 2px border.
      boxShadow: useInputColors ? 'none' : `inset 0px 0px 0px 1px ${theme.palette.primary.main}`,
    },
  };
};

export const CombinedControlsIconButtonWrapper = styled(IconButton)(({ theme }) => {
  return getCommonCombinedButtonStyles(theme);
});

export const CombinedControlsButtonWrapper = styled(Button)(({ theme }) => {
  return { ...getCommonCombinedButtonStyles(theme), fontWeight: theme.typography.fontWeightRegular };
});
